import { useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import { changeSource, type ControlledSource } from "@/api/filth-source-control-api"
import { Button } from "@/components/ui/button"

import { sourceControlKey as key, sourceControlError } from "./filth-source-control-utils"

export function FilthSourceRowActions({
  backendId,
  source,
  capabilities,
  onChanged,
  onEdit,
  onRemove,
}: {
  backendId: string
  source: ControlledSource
  capabilities: string[]
  onChanged: () => void
  onEdit: () => void
  onRemove: () => void
}) {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const action = useRef<{ operation: string; key: string } | null>(null)
  async function run(operation: "sync" | "pause" | "resume") {
    if (action.current?.operation !== operation)
      action.current = { operation, key: crypto.randomUUID() }
    setBusy(true)
    setError(null)
    try {
      await changeSource(backendId, operation, {
        body: operation === "sync" ? { mode: "incremental" } : {},
        key: action.current!.key,
        source,
      })
      action.current = null
      onChanged()
    } catch (e) {
      setError(sourceControlError(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {capabilities.includes("connector.admin") && (
          <>
            <Button
              disabled={busy || !source.enabled}
              size="sm"
              variant="outline"
              onClick={() => void run("sync")}
            >
              {t(`${key}.sync`)}
            </Button>
            <Button
              disabled={busy}
              size="sm"
              variant="outline"
              onClick={() => void run(source.enabled ? "pause" : "resume")}
            >
              {t(`${key}.${source.enabled ? "pause" : "resume"}`)}
            </Button>
          </>
        )}
        {capabilities.includes("connector.admin") && (
          <>
            <Button disabled={busy} size="sm" variant="outline" onClick={onEdit}>
              {t(`${key}.edit`)}
            </Button>
            <Button disabled={busy} size="sm" variant="outline" onClick={onRemove}>
              {t(`${key}.remove`)}
            </Button>
          </>
        )}
      </div>
      {error && (
        <p className="text-sm" role="alert">
          {t(`${key}.errors.${error}`)}
        </p>
      )}
    </div>
  )
}
